import { useEffect, useState } from 'react'
import { api } from '@/lib/api'
import { formatNumber } from '@/lib/utils'
import {
  Building2,
  AppWindow,
  Users,
  Bell,
  Activity,
} from 'lucide-react'

interface PlatformStats {
  totalTenants: number
  totalApplications: number
  totalUsers: number
  notificationsLast30Days: number
  activeLiveSessions: number
  planBreakdown: Array<{ plan: string; count: number }>
  notificationsTrend: Array<{ date: string; count: number }>
}

interface AdminActivity {
  id: string
  action: string
  resource_type: string | null
  resource_id: string | null
  details: string | null
  created_at: string
  admin_email: string | null
  admin_name: string | null
}

interface RecentTenant {
  id: string
  name: string
  slug: string
  plan: string
  created_at: string
}

interface ActivityResponse {
  adminActivity: AdminActivity[]
  recentTenants: RecentTenant[]
}

export function DashboardPage() {
  const [stats, setStats] = useState<PlatformStats | null>(null)
  const [activity, setActivity] = useState<ActivityResponse | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchData() {
      try {
        const [statsData, activityData] = await Promise.all([
          api.get<PlatformStats>('/stats'),
          api.get<ActivityResponse>('/activity/recent'),
        ])
        setStats(statsData)
        setActivity(activityData)
      } catch (error) {
        // Failed to fetch data, will show empty state
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  const statCards = [
    {
      name: 'Total Tenants',
      value: stats?.totalTenants || 0,
      icon: Building2,
      color: 'text-blue-500',
    },
    {
      name: 'Applications',
      value: stats?.totalApplications || 0,
      icon: AppWindow,
      color: 'text-green-500',
    },
    {
      name: 'End Users',
      value: stats?.totalUsers || 0,
      icon: Users,
      color: 'text-purple-500',
    },
    {
      name: 'Notifications (30d)',
      value: stats?.notificationsLast30Days || 0,
      icon: Bell,
      color: 'text-yellow-500',
    },
    {
      name: 'Active Live Sessions',
      value: stats?.activeLiveSessions || 0,
      icon: Activity,
      color: 'text-cyan-500',
    },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Platform Overview</h1>
        <p className="text-muted-foreground">
          Monitor AlertWhale platform health and metrics
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <div
            key={stat.name}
            className="bg-card border border-border rounded-lg p-4"
          >
            <div className="flex items-center gap-3">
              <div className={cn('p-2 rounded-lg bg-accent', stat.color)}>
                <stat.icon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{stat.name}</p>
                <p className="text-2xl font-bold">
                  {typeof stat.value === 'number'
                    ? formatNumber(stat.value)
                    : stat.value}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Admin Activity */}
        <div className="bg-card border border-border rounded-lg">
          <div className="border-b border-border px-4 py-3">
            <h2 className="font-semibold">Recent Admin Activity</h2>
          </div>
          <div className="divide-y divide-border">
            {!activity?.adminActivity?.length ? (
              <div className="px-4 py-8 text-center text-muted-foreground">
                No recent activity
              </div>
            ) : (
              (Array.isArray(activity.adminActivity) ? activity.adminActivity : []).slice(0, 5).map((item) => (
                <div key={item.id} className="px-4 py-3 flex items-center gap-4">
                  <div className="flex-1">
                    <p className="text-sm font-medium capitalize">
                      {item.action.replace(/_/g, ' ')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      by {item.admin_name || item.admin_email || 'System'}
                      {item.resource_type && ` on ${item.resource_type}`}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(item.created_at).toLocaleString()}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Tenants */}
        <div className="bg-card border border-border rounded-lg">
          <div className="border-b border-border px-4 py-3">
            <h2 className="font-semibold">Recently Registered Tenants</h2>
          </div>
          <div className="divide-y divide-border">
            {!activity?.recentTenants?.length ? (
              <div className="px-4 py-8 text-center text-muted-foreground">
                No recent tenants
              </div>
            ) : (
              (Array.isArray(activity.recentTenants) ? activity.recentTenants : []).map((tenant) => (
                <div key={tenant.id} className="px-4 py-3 flex items-center gap-4">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Building2 className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{tenant.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {tenant.slug} · {tenant.plan}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(tenant.created_at).toLocaleString()}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Plan Breakdown */}
      {stats?.planBreakdown && Array.isArray(stats.planBreakdown) && stats.planBreakdown.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4">
          <h2 className="font-semibold mb-4">Tenants by Plan</h2>
          <div className="flex gap-6 flex-wrap">
            {(Array.isArray(stats.planBreakdown) ? stats.planBreakdown : []).map((item) => (
              <div key={item.plan} className="text-center">
                <p className="text-2xl font-bold">{formatNumber(item.count)}</p>
                <p className="text-sm text-muted-foreground capitalize">{item.plan}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function cn(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(' ')
}
